/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import Accordion, {getAccordionWrapperStyles} from '@ion/dls/src/components/accordion/Accordion';
import {AssetDetailsSectionAccordion} from '@ion/dls/src/components/assetdetails/current/AssetDetailsSectionAccordion';
import OpenModal from '@ion/dls/src/components/buttons/OpenModal';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {IonDialog} from '@ion/dls/src/components/dialogs/IonDialog';
import {TombstoneLoader} from '@ion/dls/src/components/loading/TombstoneLoader';
import {Hidden} from '@ion/dls/src/layout/Hidden';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {AssetDetailsSection, useRegisteredAccordion} from '../../assetDetailsAccordionStore';
import NoSensorData from '../reports/NoSensorData';
import {GenericSensorData, GenericSensorLineChart} from './GenericSensorLineChart';

export type GenericSensorAccordionProps = {
  data: GenericObject;
  loading: boolean;
}

const useStyles = makeStyles(() => ({
  accordion : {
    margin  : '0px !important',
    padding : '0 15px'
  },
  accordionTitle : {
    ...dlsTypography('panelCards'),
    fontFamily    : 'Roboto',
    fontWeight    : '600',
    fontSize      : '14px',
    lineHeight    : '17px',
    textTransform : 'uppercase',
    color         : dlsColor('black'),
    minHeight     : 48
  },
  selectionContainer : {
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'space-between',
    alignItems     : 'center',
    padding        : '0 15px'
  },
  // current theme's card already pads its body 16px (ION-7461) - drops the
  // legacy side padding so the empty state aligns to the 424px content
  // width instead of doubling up ...
  currentSelectionContainer : {
    padding : 0
  },
  loader : {
    height : 48
  },
  openModalIcon : {
    padding        : '0 15px',
    display        : 'flex',
    justifyContent : 'flex-end'
  },
  currentOpenModalIcon : {
    padding : 0
  },
  expandContainer : {
    height          : 500,
    display         : 'flex',
    backgroundColor : 'white',
    overflow        : 'hidden'
  },
  expandChartContainer : {
    padding : 16,
    height  : 420,
    width   : '100%'
  },
  ...getAccordionWrapperStyles()
}));

/**
 * @GenericSensorAccordion
 *
 * This component renders the generic sensor accordion and chart.
 * If a Generic Sensor peripheral has been selected within the Admin Device editor then this component will render
 * if there is data available for the selected sensor.
 *
 * ```typescript
 *
 * import {GenericSensorAccordion} from '@ion/dls/src/routes/tracking/sc/genericsensor';
 *
 *  <GenericSensorAccordion data={data} />
 *
 * ```
 *
 * @param data - The sensor data
 * @returns
 */
const GenericSensorAccordion = ({data, loading}: GenericSensorAccordionProps) => {
  const classes                     = useStyles();
  const isCurrent                   = isCurrentTheme();
  const initialSensor               = data?.[0];
  const {id}                        = initialSensor || {};

  const [sensorType, setSensorType]               = useState<string>(id);
  const [chartData, setChartData]                 = useState<GenericSensorData>(initialSensor);
  const [isShowExpandModal, setIsShowExpandModal] = useState(false);

  // Updates the chart data and sensor type when the user selects a different sensor
  const onSensorTypeChange = useCallback(function({target: {value}}) {
    if (value) {
      const id = value?.split('-')[1].trim().toLowerCase();
      const index = data?.findIndex((sensor: {id: string}) => sensor?.id === id);
      setChartData(data[index]);
      setSensorType(value);
    }
  }, [data]);

  useEffect(() => {
    if (!isNil(data) && !isEmpty(data) && !isNil(initialSensor)) {
      setChartData(data[0]);
      setSensorType(data[0]?.id);
    }
  }, [data]);

  const onShowExpandModal = useCallback(() => {
    setIsShowExpandModal(true);
  }, []);

  const onCloseExpandModal = useCallback(() => {
    setIsShowExpandModal(false);
  }, []);

  // registers this section with the Asset Details Drawer's shared accordion
  // store so the drawer header's expand/collapse-all control can drive it -
  // only while the current theme accordion below renders, the legacy
  // Accordion stays uncontrolled ...
  const showCurrentAccordion = isCurrent && !loading;
  const {expanded, onExpand} = useRegisteredAccordion(
    AssetDetailsSection.genericSensor,
    false,
    showCurrentAccordion
  );

  const details = (
    <>
      {!isNil(chartData) ? (
        <>
          <GenericSensorLineChart data={data}
            chartData={chartData}
            onSensorTypeChange={onSensorTypeChange}
            sensorType={sensorType} />
          <Hidden show='desktop'>
            <div className={themedClassName(classes.openModalIcon, classes.currentOpenModalIcon, isCurrent)}>
              <OpenModal handleOpenModal={onShowExpandModal} />
            </div>
          </Hidden>
          {isShowExpandModal && (
            <IonDialog onClose={onCloseExpandModal}
              title={translate('tracking:genericSensor.header')}
              actions={(
                <PrimaryButton onClick={onCloseExpandModal}>
                  {translate('common:close')}
                </PrimaryButton>
              )}
              shouldRemoveDefaultContentPadding
              shouldSupportContentHeight
              shouldCloseOnBackdropClick
              open>
              <div className={classes.expandContainer}>
                <div className={classes.expandChartContainer}>
                  <GenericSensorLineChart data={data}
                    chartData={chartData}
                    sensorType={sensorType}
                    onSensorTypeChange={onSensorTypeChange}
                    isDialogRender={isShowExpandModal}/>
                </div>
              </div>
            </IonDialog>
          )}
        </>
      ) : (
        <div className={themedClassName(classes.selectionContainer, classes.currentSelectionContainer, isCurrent)}>
          <NoSensorData displayIcon={false} />
        </div>
      )}
    </>
  );

  const result = showCurrentAccordion ? (
    <AssetDetailsSectionAccordion title={translate('tracking:genericSensor.header')}
      expanded={expanded}
      onExpand={onExpand}>
      {details}
    </AssetDetailsSectionAccordion>
  ) : !loading ? (
    <div className={classes.accordionWrapper}>
      <Accordion className={classes.accordion}
        header={
          <div className={classes.accordionTitle}>
            {translate('tracking:genericSensor.header')}
          </div>
        }
        details={details}
        borderBottom={'none'}
        padding={'10px 0px'}
        isCondensedVariant />
    </div>
  ) : (
    <div className={classes.loader}>
      <TombstoneLoader />
    </div>
  );

  return result;
};

export default GenericSensorAccordion;
